import { readonly, ref, type Ref } from 'vue';

export const THEMES = ['system', 'light', 'dark'] as const;
export type Theme = (typeof THEMES)[number];

const STORAGE_KEY = 'dometry:theme';

type ThemeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface ThemeState {
  theme: Readonly<Ref<Theme>>;
  set(next: Theme): void;
  cycle(): void;
}

/**
 * Theme preference: 'system' follows the OS; 'light'/'dark' pin it with
 * data-theme on <html> (read by tokens.css). public/theme-init.js applies the
 * saved value before first paint; this keeps it in sync afterwards.
 */
export function createTheme({
  storage = safeStorage(),
  root = document.documentElement,
}: { storage?: ThemeStorage | null; root?: HTMLElement } = {}): ThemeState {
  const theme = ref<Theme>(read(storage));
  apply(root, theme.value);

  function set(next: Theme): void {
    if (!THEMES.includes(next)) throw new Error(`Unknown theme "${String(next)}"`);
    theme.value = next;
    apply(root, next);
    try {
      if (next === 'system') storage?.removeItem(STORAGE_KEY);
      else storage?.setItem(STORAGE_KEY, next);
    } catch {
      // Storage full or blocked: the in-memory choice still applies.
    }
  }

  /** Cycle system → light → dark → system. */
  function cycle(): void {
    set(THEMES[(THEMES.indexOf(theme.value) + 1) % THEMES.length] ?? 'system');
  }

  return { theme: readonly(theme), set, cycle };
}

let shared: ThemeState | undefined;
/** App-wide theme state (one instance). */
export function useTheme(): ThemeState {
  shared ??= createTheme();
  return shared;
}

function read(storage: ThemeStorage | null): Theme {
  try {
    const value = storage?.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

function apply(root: HTMLElement, value: Theme): void {
  if (value === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', value);
}

function safeStorage(): ThemeStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}
